(() => {
  if (window.__advancedGalleryCleanProductionLoaded) return;
  window.__advancedGalleryCleanProductionLoaded = true;

  const SELECTORS = {
    section: '[data-advanced-gallery]',
    track: '[data-advanced-gallery-track]',
    prev: '[data-advanced-gallery-prev]',
    next: '[data-advanced-gallery-next]',
    dots: '[data-advanced-gallery-dots]',
    progressBar: '[data-advanced-gallery-progress-bar]'
  };

  class AdvancedGallery {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector(SELECTORS.track);
      this.prevButton = section.querySelector(SELECTORS.prev);
      this.nextButton = section.querySelector(SELECTORS.next);
      this.dotsContainer = section.querySelector(SELECTORS.dots);
      this.progressBar = section.querySelector(SELECTORS.progressBar);
      this.autoplayEnabled = section.dataset.autoplay === 'true';
      this.autoplaySpeed = Number(section.dataset.autoplaySpeed || 4000);
      this.loopEnabled = section.dataset.loop === 'true';
      this.autoplayTimer = null;
      this.scrollRaf = null;
      this.resizeRaf = null;
      this.isVisible = true;
      this.isDragging = false;
      this.hasDragged = false;
      this.dragStartX = 0;
      this.dragStartScrollLeft = 0;
      this.pagePositions = [];
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.track) return;

      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleClickCapture = this.handleClickCapture.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
      this.pauseAutoplay = this.pauseAutoplay.bind(this);
      this.startAutoplay = this.startAutoplay.bind(this);

      this.init();
    }

    init() {
      if (this.prevButton) this.prevButton.addEventListener('click', this.handlePrevClick);
      if (this.nextButton) this.nextButton.addEventListener('click', this.handleNextClick);

      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.handlePointerDown);
      this.track.addEventListener('pointermove', this.handlePointerMove);
      this.track.addEventListener('pointerup', this.handlePointerUp);
      this.track.addEventListener('pointercancel', this.handlePointerUp);
      this.track.addEventListener('mouseleave', this.handlePointerUp);
      this.track.addEventListener('click', this.handleClickCapture, true);
      this.track.addEventListener('mouseenter', this.pauseAutoplay);
      this.track.addEventListener('focusin', this.pauseAutoplay);
      this.track.addEventListener('mouseleave', this.startAutoplay);
      this.track.addEventListener('focusout', this.startAutoplay);

      window.addEventListener('resize', this.handleResize);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(this.handleIntersection, { threshold: 0.15 });
        this.intersectionObserver.observe(this.section);
      }

      this.refresh();
      this.startAutoplay();
    }

    getItems() {
      return Array.from(this.track.children).filter((item) => item.classList.contains('advanced-gallery__item'));
    }

    isScrollable() {
      return this.track.scrollWidth - this.track.clientWidth > 4;
    }

    getMaxScrollLeft() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    getScrollAmount() {
      const firstItem = this.getItems()[0];
      if (!firstItem) return this.track.clientWidth;

      const itemWidth = firstItem.getBoundingClientRect().width;
      const gap = Number.parseFloat(window.getComputedStyle(this.track).columnGap || 0);
      return itemWidth + gap;
    }

    getBehavior() {
      return this.prefersReducedMotion ? 'auto' : 'smooth';
    }

    refresh() {
      this.createDots();
      this.updateControls();
    }

    handlePrevClick() {
      this.scrollPrevious();
    }

    handleNextClick() {
      this.scrollNext();
    }

    scrollPrevious() {
      if (!this.isScrollable()) return;

      if (this.loopEnabled && this.track.scrollLeft <= 4) {
        this.track.scrollTo({ left: this.getMaxScrollLeft(), behavior: this.getBehavior() });
        return;
      }

      this.track.scrollBy({ left: -this.getScrollAmount(), behavior: this.getBehavior() });
    }

    scrollNext() {
      if (!this.isScrollable()) return;

      const maxScrollLeft = this.getMaxScrollLeft();
      if (this.loopEnabled && this.track.scrollLeft >= maxScrollLeft - 4) {
        this.track.scrollTo({ left: 0, behavior: this.getBehavior() });
        return;
      }

      this.track.scrollBy({ left: this.getScrollAmount(), behavior: this.getBehavior() });
    }

    handlePointerDown(event) {
      if (event.pointerType !== 'mouse') return;
      if (!this.isScrollable()) return;
      if (event.button !== undefined && event.button !== 0) return;

      this.isDragging = true;
      this.hasDragged = false;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.track.scrollLeft;
      this.track.classList.add('is-dragging');
      this.pauseAutoplay();

      if (this.track.setPointerCapture && event.pointerId) {
        this.track.setPointerCapture(event.pointerId);
      }
    }

    handlePointerMove(event) {
      if (!this.isDragging) return;

      const distance = event.clientX - this.dragStartX;
      if (Math.abs(distance) > 4) this.hasDragged = true;
      this.track.scrollLeft = this.dragStartScrollLeft - distance;
    }

    handlePointerUp(event) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.track.classList.remove('is-dragging');

      if (this.track.releasePointerCapture && event.pointerId) {
        try {
          this.track.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture can already be released by the browser.
        }
      }

      window.setTimeout(() => {
        this.hasDragged = false;
      }, 0);

      this.startAutoplay();
    }

    handleClickCapture(event) {
      if (!this.hasDragged) return;
      event.preventDefault();
      event.stopPropagation();
    }

    createDots() {
      if (!this.dotsContainer) return;

      this.dotsContainer.innerHTML = '';
      this.pagePositions = [];

      if (!this.isScrollable()) return;

      const maxScrollLeft = this.getMaxScrollLeft();
      const pageSize = Math.max(1, this.track.clientWidth);
      const pageCount = Math.max(1, Math.ceil((maxScrollLeft + 1) / pageSize) + 1);

      for (let index = 0; index < pageCount; index += 1) {
        const position = Math.min(maxScrollLeft, index * pageSize);
        if (this.pagePositions.includes(position)) continue;

        this.pagePositions.push(position);

        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'advanced-gallery__dot';
        dot.setAttribute('aria-label', `Go to gallery page ${this.pagePositions.length}`);
        dot.addEventListener('click', () => {
          this.track.scrollTo({ left: position, behavior: this.getBehavior() });
        });

        this.dotsContainer.appendChild(dot);
      }
    }

    updateDots() {
      if (!this.dotsContainer || !this.pagePositions.length) return;

      const dots = Array.from(this.dotsContainer.querySelectorAll('.advanced-gallery__dot'));
      const currentScrollLeft = this.track.scrollLeft;
      let activeIndex = 0;
      let closestDistance = Infinity;

      this.pagePositions.forEach((position, index) => {
        const distance = Math.abs(position - currentScrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          activeIndex = index;
        }
      });

      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    updateProgress() {
      if (!this.progressBar || !this.track) return;

      const scrollWidth = this.track.scrollWidth;
      const clientWidth = this.track.clientWidth;
      const maxScrollLeft = this.getMaxScrollLeft();

      if (scrollWidth <= 0 || clientWidth <= 0 || maxScrollLeft <= 0) {
        this.progressBar.style.width = '100%';
        this.progressBar.style.left = '0%';
        return;
      }

      const thumbWidth = Math.min(100, Math.max(18, (clientWidth / scrollWidth) * 100));
      const scrollProgress = Math.min(1, Math.max(0, this.track.scrollLeft / maxScrollLeft));
      const thumbLeft = scrollProgress * (100 - thumbWidth);

      this.progressBar.style.width = `${thumbWidth}%`;
      this.progressBar.style.left = `${thumbLeft}%`;
    }

    updateControls() {
      const maxScrollLeft = this.getMaxScrollLeft();
      const currentScrollLeft = this.track.scrollLeft;
      const isScrollable = this.isScrollable();

      if (this.prevButton) {
        this.prevButton.disabled = !isScrollable || (!this.loopEnabled && currentScrollLeft <= 4);
      }

      if (this.nextButton) {
        this.nextButton.disabled = !isScrollable || (!this.loopEnabled && currentScrollLeft >= maxScrollLeft - 4);
      }

      this.updateDots();
      this.updateProgress();
    }

    handleScroll() {
      if (this.scrollRaf) return;

      this.scrollRaf = window.requestAnimationFrame(() => {
        this.updateControls();
        this.scrollRaf = null;
      });
    }

    handleResize() {
      if (this.resizeRaf) return;

      this.resizeRaf = window.requestAnimationFrame(() => {
        this.refresh();
        this.resizeRaf = null;
      });
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.pauseAutoplay();
        return;
      }

      this.startAutoplay();
    }

    handleIntersection(entries) {
      const entry = entries[0];
      this.isVisible = Boolean(entry && entry.isIntersecting);

      if (this.isVisible) {
        this.startAutoplay();
      } else {
        this.pauseAutoplay();
      }
    }

    startAutoplay() {
      if (!this.autoplayEnabled) return;
      if (this.prefersReducedMotion) return;
      if (document.hidden) return;
      if (!this.isVisible) return;
      if (!this.isScrollable()) return;

      this.pauseAutoplay();
      this.autoplayTimer = window.setInterval(() => {
        this.scrollNext();
      }, this.autoplaySpeed);
    }

    pauseAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    destroy() {
      this.pauseAutoplay();

      if (this.scrollRaf) window.cancelAnimationFrame(this.scrollRaf);
      if (this.resizeRaf) window.cancelAnimationFrame(this.resizeRaf);
      if (this.intersectionObserver) this.intersectionObserver.disconnect();

      if (this.prevButton) this.prevButton.removeEventListener('click', this.handlePrevClick);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.handleNextClick);

      this.track.removeEventListener('scroll', this.handleScroll);
      this.track.removeEventListener('pointerdown', this.handlePointerDown);
      this.track.removeEventListener('pointermove', this.handlePointerMove);
      this.track.removeEventListener('pointerup', this.handlePointerUp);
      this.track.removeEventListener('pointercancel', this.handlePointerUp);
      this.track.removeEventListener('mouseleave', this.handlePointerUp);
      this.track.removeEventListener('click', this.handleClickCapture, true);
      this.track.removeEventListener('mouseenter', this.pauseAutoplay);
      this.track.removeEventListener('focusin', this.pauseAutoplay);
      this.track.removeEventListener('mouseleave', this.startAutoplay);
      this.track.removeEventListener('focusout', this.startAutoplay);

      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  const galleries = new Map();

  function initAdvancedGalleries(container = document) {
    container.querySelectorAll(SELECTORS.section).forEach((section) => {
      if (galleries.has(section)) return;
      galleries.set(section, new AdvancedGallery(section));
    });
  }

  function destroyAdvancedGallery(section) {
    const gallery = galleries.get(section);
    if (!gallery) return;

    gallery.destroy();
    galleries.delete(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAdvancedGalleries(), { once: true });
  } else {
    initAdvancedGalleries();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAdvancedGalleries(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches(SELECTORS.section)
      ? event.target
      : event.target.querySelector(SELECTORS.section);

    if (section) destroyAdvancedGallery(section);
  });
})();
